/**
 * The account's history as one timeline (Customer 360, 02 §5.4): what the team
 * recorded, promises and how they ended, cheques, and the bills TallyPrime
 * shows as closed — newest first, each with a mark for its kind (a shape and a
 * colour, never the colour alone: the title names it), its amount on the right
 * so money events scan like a statement, and where a fact came from when it is
 * not the team's own entry.
 *
 * One event reads top to bottom (owner, 24 Oct 2026): what happened and its
 * amount; when, who, and from where; then what was said. A thin line joins the
 * marks, in place of a rule between every row.
 *
 * The right-hand rail shows the recent part of it beside every tab; Activity
 * shows all of it, with filters.
 */
import { useId, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cx, EmptyState } from '@timely/ui';
import type { TimelineEvent, TimelineMark } from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { draftStarted, useAccount } from './accountPage';
import { useTabTo } from './AccountTabs';

const MARK: Record<TimelineMark, { cls: string; word: string }> = {
    person: { cls: 'size-2 rounded-full bg-label-3', word: 'An entry by a person' },
    promise: { cls: 'size-2 bg-promise-waiting', word: 'A promise' },
    kept: { cls: 'size-2 bg-promise-kept', word: 'Kept or received' },
    broken: { cls: 'size-2 bg-promise-broken', word: 'Not kept' },
    cheque: { cls: 'size-2 border-[1.5px] border-accent bg-card', word: 'A cheque' },
    cleared: { cls: 'size-2 rotate-45 bg-promise-kept', word: 'A bill closed in TallyPrime' },
    change: { cls: 'size-2 rounded-full border-[1.5px] border-label-3 bg-card', word: 'A change to the account' },
};

export const Mark = ({ mark, className }: { mark: TimelineMark; className?: string }) => (
    <span aria-hidden="true" title={MARK[mark].word} className={cx('inline-block flex-none', MARK[mark].cls, className)} />
);

/**
 * One event. `compact` (the rail) gives its day; the whole history gives its time under the day's heading.
 * The line joining the marks runs down the left of every event but the last.
 */
export function TimelineRow({ event: e, compact = false }: { event: TimelineEvent; compact?: boolean }) {
    const { money, date } = useFormatters();
    const when = compact ? date.short(e.at) : e.dayOnly ? '' : date.time(e.at);
    const amountTone = e.mark === 'broken' ? 'text-dang' : e.mark === 'kept' || e.mark === 'cleared' ? 'text-pos' : 'text-label';
    return (
        <li className="group/event relative grid grid-cols-[0.5rem_minmax(0,1fr)_auto] gap-x-3 pb-4 last:pb-1">
            <span aria-hidden="true"
                className="absolute left-[3.5px] top-[0.65rem] -bottom-[0.4rem] w-px bg-separator group-last/event:hidden" />
            <span className="relative pt-[0.4rem] flex justify-center"><Mark mark={e.mark} /></span>
            <div className="min-w-0">
                <p className="text-secondary font-semibold text-label leading-snug break-words">{e.title}</p>
                <p className="mt-0.5 text-caption text-label-3 flex items-center gap-x-1.5 gap-y-0.5 flex-wrap">
                    {when ? <time dateTime={e.at} className="num">{when}</time> : <span className="sr-only">On the day; the time is not known</span>}
                    {e.actor && <>{when && <span aria-hidden="true">·</span>}<span>{e.actor}</span></>}
                    {e.source && (
                        <span className="inline-flex items-center rounded-sm px-1.5 h-4.5 bg-card-3 font-medium text-label-2">
                            {e.source === 'TallyPrime' ? 'From TallyPrime' : 'Cheques'}
                        </span>
                    )}
                </p>
                {e.body && (
                    // Plain text, rendered as text: React escapes it, and nothing here uses dangerouslySetInnerHTML.
                    <p className={cx('mt-1 text-secondary text-label-2 break-words whitespace-pre-line', compact && 'line-clamp-2')}>{e.body}</p>
                )}
            </div>
            <div className="text-right pl-1">
                {e.amount !== null && (
                    <>
                        <span className={cx('block text-secondary font-semibold num whitespace-nowrap leading-snug', amountTone)}>{money.exact(e.amount)}</span>
                        {e.amountNote && <span className="block mt-0.5 text-caption text-label-3 whitespace-nowrap">{e.amountNote}</span>}
                    </>
                )}
            </div>
        </li>
    );
}

/** Events grouped under their day ("Monday 19 October"), newest day first. */
export function TimelineDays({ events }: { events: readonly TimelineEvent[] }) {
    const { workspace } = useActiveWorkspace();
    const { date } = useFormatters();
    const heading = useMemo(() => {
        try {
            return new Intl.DateTimeFormat(workspace.locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
        } catch {
            return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
        }
    }, [workspace.locale]);
    const days: { day: string; events: TimelineEvent[] }[] = [];
    for (const e of events) {
        const day = e.dayOnly ? e.at : date.inputValue(e.at);
        const last = days[days.length - 1];
        if (last && last.day === day) last.events.push(e); else days.push({ day, events: [e] });
    }
    return (
        <div className="space-y-2">
            {days.map(d => (
                <section key={d.day} aria-label={heading.format(new Date(`${d.day}T00:00:00Z`))}>
                    <h4 className="pt-3 pb-3 text-caption font-semibold text-label-2">
                        {heading.format(new Date(`${d.day}T00:00:00Z`))}
                    </h4>
                    <ul>
                        {d.events.map(e => <TimelineRow key={e.id} event={e} />)}
                    </ul>
                </section>
            ))}
        </div>
    );
}

/** How many recent events the rail shows: enough to answer "what happened lately?", never the whole life. */
export const RAIL_EVENTS = 8;

/**
 * What has happened with this customer recently (the owner's rule, 2026-10-23): the rail beside Overview, Bills and
 * Contacts from 1280px, so nobody leaves the work to read it; narrower, a part of Overview. The rest is one press
 * away, in the full activity. An outcome somebody started and left is kept, and offered back here.
 */
export function ActivityRail({ limit = RAIL_EVENTS, onContinue }: {
    limit?: number;
    /** Carries on with the outcome somebody started: the rail's composer, or the dialog where there is no rail. */
    onContinue: () => void;
}) {
    const page = useAccount();
    const tabTo = useTabTo();
    const id = useId();
    const recent = page.timeline.slice(0, limit);
    const started = page.rights.record && draftStarted(page.record.draft);
    return (
        <section aria-labelledby={`${id}-title`} className="rounded-card border border-separator bg-card shadow-e0 min-w-0">
            <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 min-h-14">
                <h3 id={`${id}-title`} className="font-display stretch-title text-heading font-semibold text-label tracking-heading">Recent activity</h3>
                <Link to={tabTo('activity')} className="text-secondary font-semibold text-accent-text hover:underline whitespace-nowrap">
                    View full activity
                </Link>
            </div>
            {started && (
                <div className="mx-5 mb-3 rounded-control bg-card-2 px-3 py-2 flex items-center justify-between gap-3 text-secondary">
                    <span className="text-label-2">An outcome you started is kept.</span>
                    <button type="button" onClick={onContinue} className="font-semibold text-accent-text hover:underline whitespace-nowrap">Carry on</button>
                </div>
            )}
            {recent.length === 0 ? (
                <EmptyState
                    compact
                    title="Nothing recorded yet"
                    hint={page.rights.record
                        ? 'Record what happens when you ring, and whoever picks this account up next will know what was tried.'
                        : 'Nobody has written anything about this account yet.'}
                />
            ) : (
                <ul aria-label="Recent activity, newest first" className="px-5 pb-4">
                    {recent.map(e => <TimelineRow key={e.id} event={e} compact />)}
                </ul>
            )}
        </section>
    );
}
